import { useMemo } from "react";
import { Drawer, Form, Input, Select, Switch, Button, Space, InputNumber, Checkbox, Divider, Modal } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import styles from "../styles/NotificationDrawer.module.css";

const UNIT_OPTIONS = [
    { value: "DAY", labelKey: "notification.unit.day" },
    { value: "WEEK", labelKey: "notification.unit.week" },
    { value: "MONTH", labelKey: "notification.unit.month" },
    { value: "YEAR", labelKey: "notification.unit.year" },
];

const CONDITION_OPTIONS = [
    { value: "BEFORE", labelKey: "notification.condition.before" },
    { value: "ON", labelKey: "notification.condition.on" },
    { value: "AFTER", labelKey: "notification.condition.after" },
];

const FORM_ID = "notification-form";
const DEFAULT_TIMING = { value: 1, unit: "DAY", condition: "BEFORE" };
const dividerStyle = { margin: "20px 0" };
const rowStyle = { display: "flex", alignItems: "center", gap: 8, marginBottom: 12 };

const normalizeRule = (r) => ({
    condition: r?.condition ?? DEFAULT_TIMING.condition,
    value: r?.condition === "ON" ? 0 : r?.value ?? DEFAULT_TIMING.value,
    unit: r?.unit ?? DEFAULT_TIMING.unit,
});

/**
 * Form แยกเป็น component ของตัวเอง เพื่อให้ form instance (useForm) ถูกสร้างใหม่ทุกครั้งที่ Drawer เปิด
 * (Drawer destroyOnClose จะ unmount component นี้ตอนปิด) -> ไม่มี store ค้างจาก record ก่อนหน้า
 */
const NotificationForm = ({ record, templateOptions, emailLoopOptions, onSubmit }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const isEdit = !!record;

    const conditionOptions = useMemo(
        () => CONDITION_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) })),
        [t]
    );
    const unitOptions = useMemo(
        () => UNIT_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) })),
        [t]
    );

    const initialValues = useMemo(() => {
        if (!record) {
            return {
                isEmail: true,
                isMobile: false,
                isActive: true,
                toList: [],
                ccList: [],
                bccList: [],
                timingRuleList: [{ ...DEFAULT_TIMING }],
            };
        }
        return {
            templateId: record.templateId,
            toList: record.toList?.map((r) => r.value) || [],
            ccList: record.ccList?.map((r) => r.value) || [],
            bccList: record.bccList?.map((r) => r.value) || [],
            isEmail: record.isEmail,
            isMobile: record.isMobile,
            isActive: record.isActive === "Y",
            timingRuleList: record.timingRuleList?.length
                ? record.timingRuleList.map(normalizeRule)
                : [{ ...DEFAULT_TIMING }],
        };
    }, [record]);

    const getTemplateInfo = (selectedId) => {
        if (isEdit) {
            return {
                templateName: record.templateName,
                emailTemplateCode: record.emailTemplateCode,
                isHr: record.isHr,
            };
        }
        const selected = templateOptions.find((o) => o.value === selectedId);
        return selected
            ? {
                templateName: selected.templateName,
                emailTemplateCode: selected.emailTemplateCode,
                isHr: selected.isHr,
            }
            : { templateName: "", emailTemplateCode: "", isHr: null };
    };

    return (
        <Form
            id={FORM_ID}
            form={form}
            initialValues={initialValues}
            className={styles.form}
            layout="horizontal"
            labelCol={{ flex: "130px" }}
            wrapperCol={{ flex: "1 1 0", style: { minWidth: 0 } }}
            labelAlign="left"
            colon={false}
            onFinish={onSubmit}
        >
            <div className={styles.activeRow}>
                <span>{t("notification.label.active")}</span>
                <Form.Item name="isActive" valuePropName="checked" noStyle>
                    <Switch />
                </Form.Item>
            </div>

            <h4 className={styles.sectionTitle}>{t("notification.form.section_info")}</h4>

            {!isEdit && (
                <Form.Item
                    name="templateId"
                    label={t("notification.label.template")}
                    rules={[{ required: true, message: t("notification.form.template_required") }]}
                >
                    <Select
                        style={{ width: "100%" }}
                        placeholder={t("notification.form.select_template")}
                        options={templateOptions}
                    />
                </Form.Item>
            )}

            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.templateId !== cur.templateId}>
                {({ getFieldValue }) => {
                    const info = getTemplateInfo(getFieldValue("templateId"));
                    const recipientText =
                        info.isHr === true
                            ? t("setting_email_template_list.form.recipient_hr")
                            : info.isHr === false
                                ? t("setting_email_template_list.form.recipient_employee")
                                : "";
                    return (
                        <>
                            <Form.Item label={t("notification.label.name")}>
                                <Input value={info.templateName} disabled />
                            </Form.Item>
                            <Form.Item label={t("notification.label.event")}>
                                <Input value={info.emailTemplateCode} disabled />
                            </Form.Item>
                            <Form.Item label={t("notification.label.recipient_main")}>
                                <Input value={recipientText} disabled />
                            </Form.Item>
                        </>
                    );
                }}
            </Form.Item>

            <Divider style={dividerStyle} />
            <h4 className={styles.sectionTitle}>{t("notification.form.section_recipient")}</h4>
            <div className={styles.hint}>{t("notification.form.recipient_hint")}</div>

            <Form.Item
                name="toList"
                label="To"
                required
                rules={[{ required: true, message: t("notification.form.to_required") }]}
            >
                <Select mode="multiple" allowClear options={emailLoopOptions} placeholder={t("notification.form.select_recipient")} />
            </Form.Item>
            <Form.Item name="ccList" label="CC">
                <Select mode="multiple" allowClear options={emailLoopOptions} placeholder={t("notification.form.select_recipient")} />
            </Form.Item>
            <Form.Item name="bccList" label="BCC">
                <Select mode="multiple" allowClear options={emailLoopOptions} placeholder={t("notification.form.select_recipient")} />
            </Form.Item>

            <Divider style={dividerStyle} />
            <h4 className={styles.sectionTitle}>{t("notification.form.section_timing")}</h4>
            <div className={styles.hint}>{t("notification.form.timing_hint")}</div>

            <Form.List name="timingRuleList">
                {(fields, { add, remove }) => (
                    <>
                        {fields.map(({ key, name }) => (
                            <div key={key} style={rowStyle}>
                                <Form.Item name={[name, "condition"]} noStyle>
                                    <Select
                                        style={{ flex: 1, minWidth: 0 }}
                                        options={conditionOptions}
                                        onChange={(val) => {
                                            if (val === "ON") {
                                                form.setFieldValue(["timingRuleList", name, "value"], 0);
                                            }
                                        }}
                                    />
                                </Form.Item>
                                <Form.Item
                                    noStyle
                                    shouldUpdate={(p, c) =>
                                        p.timingRuleList?.[name]?.condition !== c.timingRuleList?.[name]?.condition
                                    }
                                >
                                    {({ getFieldValue }) => {
                                        const isOn = getFieldValue(["timingRuleList", name, "condition"]) === "ON";
                                        return (
                                            <>
                                                <Form.Item name={[name, "value"]} noStyle>
                                                    <InputNumber min={0} disabled={isOn} style={{ width: 80 }} />
                                                </Form.Item>
                                                <Form.Item name={[name, "unit"]} noStyle>
                                                    <Select style={{ width: 110 }} disabled={isOn} options={unitOptions} />
                                                </Form.Item>
                                            </>
                                        );
                                    }}
                                </Form.Item>
                                <DeleteOutlined onClick={() => remove(name)} style={{ color: "#F04438", cursor: "pointer" }} />
                            </div>
                        ))}
                        <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ ...DEFAULT_TIMING })} block>
                            {t("notification.form.add_timing")}
                        </Button>
                    </>
                )}
            </Form.List>

            <Divider style={dividerStyle} />
            <h4 className={styles.sectionTitle}>{t("notification.form.section_channel")}</h4>
            <Space size={32}>
                <Form.Item name="isEmail" valuePropName="checked" noStyle>
                    <Checkbox>{t("notification.channel.email")}</Checkbox>
                </Form.Item>
                <Form.Item name="isMobile" valuePropName="checked" noStyle>
                    <Checkbox>{t("notification.channel.mobile")}</Checkbox>
                </Form.Item>
            </Space>
        </Form>
    );
};

const NotificationDrawer = ({
    open,
    record,
    templateOptions = [],
    emailLoopOptions = [],
    onCancel,
    onSubmit,
}) => {
    const { t } = useTranslation();
    const isEdit = !!record;

    return (
        <Modal
            title={isEdit ? t("notification.form.title_edit") : t("notification.form.title_create")}
            open={open}
            onCancel={onCancel}
            width={750}
            centered
            destroyOnClose
            styles={{ body: { maxHeight: "75vh", overflowY: "auto" } }}
            footer={
                <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                    <Button onClick={onCancel}>{t("action.cancel")}</Button>
                    <Button type="primary" htmlType="submit" form={FORM_ID}>{t("action.save")}</Button>
                </Space>
            }
        >
            <NotificationForm
                key={record?.id ?? "new"}
                record={record}
                templateOptions={templateOptions}
                emailLoopOptions={emailLoopOptions}
                onSubmit={onSubmit}
            />
        </Modal>
    );
};

export default NotificationDrawer;